import { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { renderMetaTags, useQuerySubscription } from 'react-datocms';
import { request, hasContent } from 'lib/datocms';
import { parseSlug } from 'lib/utils';
import { PAGE_SIZE } from 'lib/constants';
import ReactPaginate from 'react-paginate';
import {
  metaTagsFragment,
  contentBlocks,
  pageHeroImageFragment,
  contentFragments,
  responsiveImageFragment,
  pageTileProps,
  menuFragment,
  pageCTAFragment,
} from 'lib/fragments';

import { Container, PageIntro, Layout, GeneralPageBody, PageTile, PageHero, PageCTA } from 'components';

export async function getStaticPaths() {
  const data = await request({ query: `{ meta: _allNewsArticlesMeta { count } }` });
  const paths = Array.from({ length: Math.ceil(data.meta.count / parseFloat(PAGE_SIZE)) }, (_, i) => i);

  return {
    fallback: 'blocking',
    paths: paths.map((page) => ({
      params: { page: page.toString() },
    })),
  };
}

export async function getStaticProps({ params, preview = false }) {
  const page = !!params && !!params.page ? parseInt(params.page) : 0;
  const first = PAGE_SIZE;
  const skip = page * PAGE_SIZE;

  const graphqlRequest = {
    query: `
      query NewsLandingPage($first: IntType, $skip: IntType) {
        menu {
          ...menuFragment
        }
        newsLandingPage {
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          ${pageHeroImageFragment}
          title
          introduction(markdown: true)
          slug
          content {
            ${contentBlocks}
          }
          callToAction {
            ...pageCTAFragment
          }
        }
        articles: allNewsArticles(first: $first, skip: $skip, orderBy: date_DESC) {
          ${pageTileProps}
          introduction(markdown: false)
          date
        }
        meta: _allNewsArticlesMeta {
          count
        }
      }

      ${metaTagsFragment}
      ${contentFragments}
      ${menuFragment}
      ${responsiveImageFragment}
      ${pageCTAFragment}
    `,
    preview,
    variables: {
      first: first,
      skip: skip,
    },
  };

  return {
    props: {
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData: await request(graphqlRequest),
            token: process.env.NEXT_DATOCMS_API_TOKEN,
          }
        : {
            enabled: false,
            initialData: await request(graphqlRequest),
          },
    },
    revalidate: 120,
  };
}

export default function News({ subscription }) {
  const router = useRouter();

  const {
    data: { site, newsLandingPage, articles, meta, menu },
  } = useQuerySubscription(subscription);

  const totalArticles = meta.count;
  const totalPages = Math.ceil(totalArticles / parseFloat(PAGE_SIZE));
  const currentPage = !!router.query && !!router.query.page ? parseInt(router.query.page) : 0;
  const pagesShowingStart = currentPage * PAGE_SIZE;
  const pagesShowingEnd = Math.min(pagesShowingStart + PAGE_SIZE, totalArticles);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // Prefetch paginated pages (PRODUCTION ONLY)
    for (let i = 0; i < totalPages; i++) {
      router.prefetch(`/news/p/${i}`);
    }
  }, [totalPages]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const onPageChange = (page) => {
    router.push({
      pathname: '/news/p/[page]',
      query: {
        page: page.selected.toString(),
      },
    });
  };

  const heroImage = newsLandingPage.image;
  const metaTags = newsLandingPage.seo;
  const callToAction = newsLandingPage.callToAction;

  return (
    <Layout preview={subscription.preview} menu={menu}>
      {metaTags && <Head>{renderMetaTags(metaTags)}</Head>}

      {heroImage && <PageHero responsiveImage={heroImage.responsiveImage} />}

      <PageIntro title={newsLandingPage.title} introduction={newsLandingPage.introduction} />

      <section className="PageBody PageBody--news">
        {hasContent(newsLandingPage) && <GeneralPageBody content={newsLandingPage.content} />}

        <Container>
          <div className="Grid u-flex u-flexWrap u-flexAlignItemsStretch">
            {articles.map((page) => (
              <div
                className="Grid-cell u-md-width1of3 u-xl-width1of4 u-mB-sm u-lg-mB-md"
                key={`page-tile-${page.slug}`}
              >
                <PageTile
                  classNames={'NewsTile'}
                  title={page.title || page.name || ''}
                  image={page.image}
                  url={parseSlug(page)}
                  date={page.date || ''}
                  introduction={page.introduction || ''}
                  portrait
                />
              </div>
            ))}
          </div>
          <div className="Pagination">
            <ReactPaginate
              previousLabel={''}
              nextLabel={''}
              breakLabel={'...'}
              breakClassName={'Pagination-item Pagination-item--break'}
              pageCount={Math.ceil(totalArticles / parseFloat(PAGE_SIZE))}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={onPageChange}
              containerClassName={'Pagination-list'}
              pageClassName={'Pagination-item'}
              previousClassName={'Pagination-item Pagination-item--back'}
              nextClassName={'Pagination-item Pagination-item--forward'}
              activeClassName={'is-active'}
              disabledClassName={'is-disabled'}
              forcePage={currentPage}
            />
            <p className="Pagination-text">
              Showing {pagesShowingStart + 1}-{pagesShowingEnd} of {totalArticles} results
            </p>
          </div>
        </Container>
      </section>

      {!!callToAction && (
        <PageCTA
          title={callToAction.title}
          caption={callToAction.caption}
          btnLabel={callToAction.buttonLabel}
          btnURL={callToAction.buttonUrl}
        />
      )}
    </Layout>
  );
}
